import { ChangeDetectorRef, Component, OnDestroy, OnInit, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, Validators, ReactiveFormsModule, FormArray } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { WebsiteConfigFormExpansionPanelComponent } from '../website-config-form-expansion-panel/website-config-form-expansion-panel.component';
import { WebsiteStateService } from '../../../../../state-management/services/website-state.service';
import { BusinessConfiguration, ContactData, LegalData, LegalDataOption } from '../../../../../state-management/model/interfaces/state.model';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BusinessType } from '../../../../../state-management/model/enums/state.enums';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { validateNamesInFormArray } from './form-validators';
import { MatDividerModule } from '@angular/material/divider';
import { Subscription } from 'rxjs';


// TODO: SVI -  Should be merged with the existing interfaces in the state.model.ts file
interface FormData {
  contactData: {
    name: string | null;
    street: string | null;
    city: string | null;
    phone: string | null;
    mobile: string | null;
    email: string | null;
  },
  businessData: {
    businessName: string | null;
    businessType: BusinessType;
    vatId: string | null;
    registrationNumber: string | null;
    registrationCity: string | null;
    businessStreet: LegalDataOption<string | null>;
    businessCity: LegalDataOption<string | null>;
    useImprintTemplate: boolean;
    businessContactPhone: LegalDataOption<string | null>;
    businessContactEmail: LegalDataOption<string | null>;
    businessContactNames: { firstname: string; surname: string }[]
  }
}

@Component({
  selector: 'app-website-config-form-contact-data',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    MatSelect,
    MatOption,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatDividerModule,
    ReactiveFormsModule,
    WebsiteConfigFormExpansionPanelComponent
  ],
  templateUrl: './website-config-form-contact-data.component.html',
  styleUrl: './website-config-form-contact-data.component.scss'
})
export class WebsiteConfigFormContactDataComponent implements OnInit, OnDestroy {

  private subs = new Subscription();

  private metaData: Signal<BusinessConfiguration> = this.websiteState.configuration;
  private contactData: ContactData = this.metaData().contactData;
  private legalData: LegalData = this.metaData().legalData;
  private debounceTimer: any;

  public form!: FormGroup;
  public businessTypes = Object.values(BusinessType);
  // TODO: SVI - Should be implemented as a signal
  public businessContactLabel: string = this.getLabelForBusinessType(this.getBusinessTypeFromString(this.legalData.businessType));

  constructor(
    private websiteState: WebsiteStateService, 
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder) { }

  ngOnInit(): void {
    this.form = this.fb.group({
      contactData: this.fb.group({
        name: [this.contactData.name, Validators.required],
        street: [this.contactData.street, Validators.required],
        city: [this.contactData.city, Validators.required],
        phone: [this.contactData.phone],
        mobile: [this.contactData.mobile],
        email: [this.contactData.email, Validators.email],
      }),
      businessData: this.fb.group({
        businessName: [this.legalData.businessName, Validators.required],
        businessType: [this.legalData.businessType, Validators.required],
        vatId: [this.legalData.vatId],
        registrationNumber: [this.legalData.registrationNumber],
        registrationCity: [this.legalData.registrationCity],

        businessStreet: this.createSyncableControl(this.legalData.businessStreet),
        businessCity: this.createSyncableControl(this.legalData.businessCity),
        businessContactPhone: this.createSyncableControl(this.legalData.businessContactPhone),
        businessContactEmail: this.createSyncableControl(this.legalData.businessContactEmail, [Validators.email]),
        businessContactNames: this.fb.array(
          this.legalData.businessContactNames.map(name =>
            this.fb.group({
              firstname: [name.firstname],
              surname: [name.surname],
            })
          ), [validateNamesInFormArray()]
        ),
      })
    });

    this.updateForm();

    this.subs.add(
      this.form.get('businessData.businessType')?.valueChanges.subscribe((type: BusinessType) =>
        this.businessContactLabel = this.getLabelForBusinessType(type)
      )
    )

    this.subs.add(
      this.form.valueChanges.subscribe((formData) => {
        console.log('CONTACT DATA CHANGES:', formData);
        if (this.form.valid) {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(async () => {
            // first update syncable form fields and then save changes
            this.updateForm();
            await this.saveFormChanges(formData);
          }, 300);
        } else {
          console.log('CONTACT DATA CHANGES: Formular ungültig, keine Daten gespeichert.');
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private createSyncableControl(initValue: LegalDataOption<string | null>, validators: any[] = []): FormGroup {
    return this.fb.group({
      value: [{ value: initValue.value, disabled: initValue.syncWithContactData }, ...validators],
      syncWithContactData: initValue.syncWithContactData
    });
  }

  get businessContactNames(): FormArray {
    return this.form.get('businessData.businessContactNames') as FormArray;
  }

  public addBusinessContactName(): void {
    this.businessContactNames.push(this.fb.group({ firstname: "", surname: "" }));
  }

  public removeBusinessContactName(index: number): void {
    this.businessContactNames.removeAt(index);
  }

  private getBusinessTypeFromString(value: string): BusinessType {
    if (Object.values(BusinessType).includes(value as BusinessType)) {
      return value as BusinessType;
    }
    console.warn(`Ungültiger Geschäftstyp: "${value}". Es wird "Selbstständig" als Standard verwendet.`);
    return BusinessType.SelfEmployed; // Standardwert
  }

  private getLabelForBusinessType(businessType: BusinessType): string {
    switch (businessType) {
      case BusinessType.SelfEmployed:
        return 'Inhaber';
      case BusinessType.GbR:
      case BusinessType.UG:
      case BusinessType.GmbH:
        return 'Geschäftsführer';
      default:
        return 'Unternehmensvertreter';
    }
  }

  public toggleInput(formGroupName: string): void {
    this.form.get(`${formGroupName}.value`)?.[this.form.get(`${formGroupName}.syncWithContactData`)?.value ? 'disable' : 'enable']({ emitEvent: false });
  }

  private updateValueIfSyncWithContactData(controlName: string, correspondingValue: any): void {
    const syncControl = this.form.get(`${controlName}.syncWithContactData`);
    const valueControl = this.form.get(`${controlName}`)?.get('value');
    if (syncControl?.value && valueControl) {
      if (valueControl.value != correspondingValue) {
        valueControl.setValue(correspondingValue, { emitEvent: false });
      }
    }
  }

  private updateForm(): void {
    this.updateValueIfSyncWithContactData('businessData.businessStreet', this.form.get('contactData.street')?.value);
    this.updateValueIfSyncWithContactData('businessData.businessCity', this.form.get('contactData.city')?.value);
    this.updateValueIfSyncWithContactData('businessData.businessContactPhone', this.form.get('contactData.phone')?.value);
    this.updateValueIfSyncWithContactData('businessData.businessContactEmail', this.form.get('contactData.email')?.value);
  }

  private async saveFormChanges(formData: FormData): Promise<void> {
    const businessCityValue: string | null = this.form.get('businessData.businessCity.value')?.value;
    const managerContentCity: string | null = this.metaData().legalData.managerContentCity.syncWithContactData ? businessCityValue : this.metaData().legalData.managerContentCity.value;
    const managerDataPrivacyCity: string | null = this.metaData().legalData.managerDataPrivacyCity.syncWithContactData ? businessCityValue : this.metaData().legalData.managerDataPrivacyCity.value;

    const businessStreetValue: string | null = this.form.get('businessData.businessStreet.value')?.value;
    const managerContentStreet: string | null = this.metaData().legalData.managerContentStreet.syncWithContactData ? businessStreetValue : this.metaData().legalData.managerContentStreet.value;
    const managerDataPrivacyStreet: string | null = this.metaData().legalData.managerDataPrivacyStreet.syncWithContactData ? businessStreetValue : this.metaData().legalData.managerDataPrivacyStreet.value;

    const businessContactEmailValue: string | null = this.form.get('businessData.businessContactEmail.value')?.value;
    const managerContentEmail: string | null = this.metaData().legalData.managerContentEmail.syncWithContactData ? businessContactEmailValue : this.metaData().legalData.managerContentEmail.value;
    const managerDataPrivacyEmail: string | null = this.metaData().legalData.managerDataPrivacyEmail.syncWithContactData ? businessContactEmailValue : this.metaData().legalData.managerDataPrivacyEmail.value;

    const businessContactPhoneValue: string | null = this.form.get('businessData.businessContactPhone.value')?.value;
    const managerContentPhone: string | null = this.metaData().legalData.managerContentPhone.syncWithContactData ? businessContactPhoneValue : this.metaData().legalData.managerContentPhone.value;
    const managerDataPrivacyPhone: string | null = this.metaData().legalData.managerDataPrivacyPhone.syncWithContactData ? businessContactPhoneValue : this.metaData().legalData.managerDataPrivacyPhone.value;

    const businessName = formData.businessData.businessName;
    const managerContentName: string | null = this.metaData().legalData.managerContentName.syncWithContactData ? businessName : this.metaData().legalData.managerContentName.value;
    const managerDataPrivacyName: string | null = this.metaData().legalData.managerDataPrivacyName.syncWithContactData ? businessName : this.metaData().legalData.managerDataPrivacyName.value;

    this.websiteState.updateConfiguration({

      contactData: {
        ...this.metaData().contactData,
        name: formData.contactData.name,
        street: formData.contactData.street,
        city: formData.contactData.city,
        phone: formData.contactData.phone,
        mobile: formData.contactData.mobile,
        email: formData.contactData.email,
      },
      legalData: {
        ...this.metaData().legalData,
        businessType: formData.businessData.businessType,
        vatId: formData.businessData.vatId,
        registrationNumber: formData.businessData.registrationNumber,
        registrationCity: formData.businessData.registrationCity,
        businessContactNames: formData.businessData.businessContactNames,
        
        // changes in these values might need to be reflected in the corresponding manager and data privacy fields
        businessName: businessName,
        managerContentName: {...this.metaData().legalData.managerContentName, value: managerContentName },
        managerDataPrivacyName: {...this.metaData().legalData.managerDataPrivacyName, value: managerDataPrivacyName },

        businessCity: { value: businessCityValue, syncWithContactData: this.form.get('businessData.businessCity.syncWithContactData')?.value },
        managerContentCity: {...this.metaData().legalData.managerContentCity, value: managerContentCity },
        managerDataPrivacyCity: {...this.metaData().legalData.managerDataPrivacyCity, value: managerDataPrivacyCity },

        businessStreet: { value: businessStreetValue, syncWithContactData: this.form.get('businessData.businessStreet.syncWithContactData')?.value },
        managerContentStreet: {...this.metaData().legalData.managerContentStreet, value: managerContentStreet },
        managerDataPrivacyStreet: {...this.metaData().legalData.managerDataPrivacyStreet, value: managerDataPrivacyStreet },

        businessContactEmail: { value: businessContactEmailValue, syncWithContactData: this.form.get('businessData.businessContactEmail.syncWithContactData')?.value },
        managerContentEmail: {...this.metaData().legalData.managerContentEmail, value: managerContentEmail },
        managerDataPrivacyEmail: {...this.metaData().legalData.managerDataPrivacyEmail, value: managerDataPrivacyEmail },

        businessContactPhone: { value: businessContactPhoneValue, syncWithContactData: this.form.get('businessData.businessContactPhone.syncWithContactData')?.value },
        managerContentPhone: {...this.metaData().legalData.managerContentPhone, value: managerContentPhone },
        managerDataPrivacyPhone: {...this.metaData().legalData.managerDataPrivacyPhone, value: managerDataPrivacyPhone },

      },
    });
  }
}